import { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Badge } from 'antd';
import {
  DashboardOutlined,
  ProjectOutlined,
  FileTextOutlined,
  SettingOutlined,
  TeamOutlined,
  AuditOutlined,
  RocketOutlined,
  AppstoreOutlined,
  TagOutlined,
  FormOutlined,
  BellOutlined,
  MessageOutlined,
  GithubOutlined,
  EditOutlined,
  BarChartOutlined,
  ContactsOutlined,
} from '@ant-design/icons';
import useUiStore from '../../../store/uiStore';
import useChatStore from '../../../store/chatStore';
import useAuthStore from '../../../store/authStore';

const { Sider } = Layout;

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarCollapsed, toggleSidebar } = useUiStore();
  const unreadCounts = useChatStore((s) => s.unreadCounts);
  const currentOrganization = useAuthStore((s) => s.currentOrganization);
  const perms = currentOrganization?.permissions || [];
  const canAdmin = perms.includes('org.manage') || perms.includes('org.manage_members') || perms.includes('org.manage_roles') || perms.includes('org.view_reports');
  const totalUnread = Object.values(unreadCounts).reduce((sum, c) => sum + c, 0);

  const menuItems = useMemo(() => {
    const items = [
      {
        key: '/',
        icon: <DashboardOutlined />,
        label: 'Dashboard',
      },
      {
        key: '/projects',
        icon: <ProjectOutlined />,
        label: 'Projects',
      },
      {
        key: '/spaces',
        icon: <FileTextOutlined />,
        label: 'Knowledge Base',
      },
      {
        key: '/blogs',
        icon: <EditOutlined />,
        label: 'Blog',
      },
      {
        key: '/leads',
        icon: <ContactsOutlined />,
        label: 'Leads',
      },
      {
        key: '/chat',
        icon: <MessageOutlined />,
        label: totalUnread > 0
          ? <span>Chat <Badge count={totalUnread} size="small" offset={[6, 0]} /></span>
          : 'Chat',
      },
    ];

    items.push({ type: 'divider' });

    if (canAdmin) {
      items.push({
        key: 'admin',
        icon: <SettingOutlined />,
        label: 'Administration',
        children: [
          { key: '/admin/organization', icon: <RocketOutlined />, label: 'Organization' },
          { key: '/admin/teams', icon: <TeamOutlined />, label: 'Teams' },
          { key: '/admin/roles', icon: <AuditOutlined />, label: 'Roles & Permissions' },
          { key: '/admin/issue-types', icon: <AppstoreOutlined />, label: 'Ticket Types' },
          { key: '/admin/labels', icon: <TagOutlined />, label: 'Labels' },
          { key: '/admin/custom-fields', icon: <FormOutlined />, label: 'Custom Fields' },
          { key: '/admin/notifications', icon: <BellOutlined />, label: 'Notification Defaults' },
          { key: '/admin/audit', icon: <FileTextOutlined />, label: 'Audit Log' },
          { key: '/admin/reports', icon: <BarChartOutlined />, label: 'Reports' },
          { key: '/admin/integrations', icon: <GithubOutlined />, label: 'Integrations' },
        ],
      });
    } else {
      items.push({
        key: 'settings',
        icon: <SettingOutlined />,
        label: 'Settings',
        children: [
          { key: '/admin/notifications', icon: <BellOutlined />, label: 'Notifications' },
          { key: '/admin/teams', icon: <TeamOutlined />, label: 'Teams' },
        ],
      });
    }

    return items;
  }, [totalUnread, canAdmin]);

  const selectedKey = useMemo(() => {
    const path = location.pathname;
    if (path === '/') return '/';
    const match = menuItems.find((item) => {
      if (item.children) {
        return item.children.some((child) => path.startsWith(child.key));
      }
      return item.key !== '/' && path.startsWith(item.key);
    });
    if (match?.children) {
      const child = match.children.find((c) => path.startsWith(c.key));
      return child?.key || path;
    }
    return match?.key || path;
  }, [location.pathname, menuItems]);

  return (
    <Sider
      collapsible
      collapsed={sidebarCollapsed}
      onCollapse={toggleSidebar}
      width={240}
      collapsedWidth={80}
      className="app-sider"
      style={{ height: '100vh', position: 'fixed', left: 0, top: 0, bottom: 0 }}
    >
      <div className="app-logo" onClick={() => navigate('/')}>
        <img src="/transparent_m.PNG" alt="MkraftOne" style={{ height: 26, width: 26, objectFit: 'contain', verticalAlign: 'middle' }} />
        {!sidebarCollapsed && <h2>MkraftOne</h2>}
      </div>

      <div className="sidebar-menu-wrapper">
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          className="sidebar-menu"
          onClick={({ key }) => navigate(key)}
        />
      </div>
    </Sider>
  );
};

export default Sidebar;
